import React, { useState, useEffect, useRef } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';

const GET_AUTOCOMPLETE_RESULTS = gql`
    query getAutocompleteResults($inputText: String!) {
        products(search: $inputText, currentPage: 1, pageSize: 3) {
            aggregations {
                label
                count
                attribute_code
                options {
                    label
                    value
                }
                position
            }
            items {
                id
                uid
                sku
                name
                small_image {
                    url
                }
                url_key
                url_suffix
                price {
                    regularPrice {
                        amount {
                            value
                            currency
                        }
                    }
                }
                price_range {
                    maximum_price {
                        final_price {
                            currency
                            value
                        }
                        discount {
                            amount_off
                        }
                    }
                }
            }
            page_info {
                total_pages
            }
            total_count
        }
    }
`;

const SearchBox = ({ onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  const { data, loading, error } = useQuery(GET_AUTOCOMPLETE_RESULTS, {
    variables: { inputText: searchTerm },
    skip: searchTerm.length < 2
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowResults(value.length >= 2);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
      onClose && onClose();
    }
  };

  const products = data?.products?.items || [];
  const totalCount = data?.products?.total_count || 0;

  return (
    <div ref={searchRef} style={{ position: 'relative', width: '100%' }}>
      <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
          placeholder="Search products..."
          style={{
            width: '100%',
            padding: '0.75rem 2.5rem 0.75rem 1rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '1rem',
            outline: 'none',
            transition: 'border-color 0.2s',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            searchTerm.length >= 2 && setShowResults(true);
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#d1d5db';
          }}
        />
        <button
          type="submit"
          style={{
            position: 'absolute',
            right: '0.5rem',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: '#7c3aed',
            cursor: 'pointer',
            fontSize: '1.2rem',
            padding: '0.25rem'
          }}
        >
          🔍
        </button>
      </form>

      {/* Autocomplete Results */}
      {showResults && searchTerm.length >= 2 && (
        <div
          ref={resultsRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'white',
            border: '1px solid #d1d5db',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1000
          }}
        >
          {loading && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
              Searching...
            </div>
          )}

          {error && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#ef4444' }}>
              Search error: {error.message}
            </div>
          )}

          {!loading && !error && products.length === 0 && searchTerm.length >= 2 && (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280' }}>
              No products found for "{searchTerm}"
            </div>
          )}

          {!loading && !error && products.length > 0 && (
            <>
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/${product.url_key}${product.url_suffix || ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    textDecoration: 'none',
                    color: 'inherit',
                    borderBottom: '1px solid #f3f4f6',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    const linkElement = e.currentTarget;
                    linkElement.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    const linkElement = e.currentTarget;
                    linkElement.style.backgroundColor = 'transparent';
                  }}
                  onClick={() => {
                    setShowResults(false);
                    onClose && onClose();
                  }}
                >
                  <img
                    src={product.small_image?.url || '/placeholder.jpg'}
                    alt={product.name}
                    style={{
                      width: '40px',
                      height: '40px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      marginRight: '0.75rem'
                    }}
                  />
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{
                      fontWeight: '500',
                      fontSize: '0.9rem',
                      lineHeight: '1.3',
                      color: '#333',
                      display: 'block',
                      overflow: 'visible',
                      whiteSpace: 'normal',
                      textOverflow: 'initial'
                    }}>
                      {product.name || `[Name missing for product: ${product.id}]`}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#7c3aed', fontWeight: 'bold', marginLeft: '1rem' }}>
                      ${product.price_range?.maximum_price?.final_price?.value || 'N/A'}
                    </div>
                  </div>
                </Link>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;